import { useState } from 'react'
import { describeLifetime, isRole, lifetimeState, roleHasCapability } from '@qim/shared'
import { setLifetime } from '../api.js'
import { formatInstant } from '../format.js'
import type { LifetimeMark } from '@qim/shared'
import type { JSX } from 'react'

/**
 * How long an instance was meant to live, and whether it has outlived that
 * (IM-134).
 *
 * ## Why this is not a verdict
 *
 * The standing rule is that nothing on these screens calls an instance waste.
 * This does not break it — it resolves it. Age alone cannot say whether an
 * instance was supposed to still be here: a Qrvey demo kept for a prospect
 * legitimately runs for months, and an alert that fires on it daily is one
 * people mute. So the *owner* states what it was for, and the screen reports the
 * arithmetic: "you asked for 7 days, it has been 34". Nobody is guessing.
 *
 * ## Three states, not two
 *
 * `unset` is its own answer and is neither within nor over. Its action is to set
 * one — which helps — rather than to defend an instance against an accusation,
 * which does not. Every instance that exists today is in it.
 *
 * `indefinite` is a deliberate choice, recorded and attributed. Some internal
 * instances genuinely are permanent, and making that a decision somebody made is
 * the whole difference between a fleet where nothing is flagged and one where
 * the flags mean something.
 */

const canSet = (role: string): boolean => isRole(role) && roleHasCapability(role, 'records:manage')

/** The short form for a table cell: within, over by N, or nothing set. */
export const LifetimeCell = ({
  lifetime,
  now = new Date(),
}: {
  readonly lifetime?: LifetimeMark
  readonly now?: Date
}): JSX.Element => {
  const state = lifetimeState(lifetime, now)
  switch (state.state) {
    case 'unset':
      // Not "—", which reads as a missing value. This is a prompt.
      return <span className="muted">No lifetime set</span>
    case 'indefinite':
      return <span className="muted">Indefinite</span>
    case 'within':
      return (
        <span>
          {state.daysLeft} day{state.daysLeft === 1 ? '' : 's'} left
        </span>
      )
    case 'over':
      // A fact, said plainly: what was asked for, and how far past it is.
      return (
        <span className="state state-attention">
          {state.daysOver} day{state.daysOver === 1 ? '' : 's'} over{' '}
          {describeLifetime(state.mark.asked)}
        </span>
      )
  }
}

/** The options a person picks from. Indefinite is offered and is not the default. */
const CHOICES: readonly { readonly label: string; readonly value: string }[] = [
  { label: 'Choose…', value: '' },
  { label: '7 days', value: '7' },
  { label: '14 days', value: '14' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'Indefinite', value: 'indefinite' },
]

/** What a choice means, for sending and for saying back. */
export const lifetimeFor = (value: string): { days: number } | { indefinite: true } | undefined => {
  if (value === '') return undefined
  if (value === 'indefinite') return { indefinite: true }
  const days = Number.parseInt(value, 10)
  return Number.isInteger(days) && days > 0 ? { days } : undefined
}

/**
 * The picker, for the run form.
 *
 * Offered only where a run creates a *new* deployment: creation is the one
 * moment the person knows the answer, and a field on every run would get an
 * answer typed by somebody guessing on behalf of a colleague.
 */
export const LifetimePicker = ({
  value,
  onChange,
}: {
  readonly value: string
  readonly onChange: (value: string) => void
}): JSX.Element => (
  <div className="field">
    <label htmlFor="runLifetime">How long is this meant to live?</label>
    <select
      id="runLifetime"
      value={value}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      aria-describedby="runLifetime-hint"
    >
      {CHOICES.map((choice) => (
        <option key={choice.value} value={choice.value}>
          {choice.label}
        </option>
      ))}
    </select>
    <span id="runLifetime-hint" className="hint">
      Optional, and changeable afterwards. It is reported as a fact later &mdash; &ldquo;asked for 7
      days, it has been 34&rdquo; &mdash; and nothing is ever destroyed because of it. Leaving it
      unset reads as &ldquo;no lifetime set&rdquo;, which is what prompts somebody to answer.
    </span>
  </div>
)

/**
 * The control on an instance's own screen.
 *
 * Changeable because a demo that becomes a POC is the normal case rather than an
 * exception — and a lifetime that could only be set once is one everybody sets
 * to indefinite.
 */
export const LifetimeControl = ({
  baseUrl,
  tenantId,
  accountKey,
  deploymentId,
  lifetime,
  role,
  onChanged,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly accountKey: string
  readonly deploymentId: string
  readonly lifetime?: LifetimeMark
  readonly role: string
  readonly onChanged: () => void
}): JSX.Element => {
  const [choice, setChoice] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  const save = async (): Promise<void> => {
    const asked = lifetimeFor(choice)
    if (asked === undefined) return
    setBusy(true)
    setProblem(undefined)
    try {
      await setLifetime(baseUrl, tenantId, accountKey, deploymentId, asked)
      setChoice('')
      // Re-read rather than splicing: what the instance says is the API's
      // answer, which is the arrangement every other write in this console uses.
      onChanged()
    } catch {
      setProblem('Could not record that just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <dt>Expected lifetime</dt>
      <dd>
        <LifetimeCell {...(lifetime === undefined ? {} : { lifetime })} />
        {lifetime === undefined ? null : (
          <p className="muted">
            {describeLifetime(lifetime.asked)}, set by {lifetime.by} on{' '}
            <time dateTime={lifetime.at}>{formatInstant(lifetime.at)}</time>
          </p>
        )}
        {canSet(role) ? (
          <p>
            <select
              aria-label="Change the expected lifetime"
              value={choice}
              disabled={busy}
              onChange={(event) => {
                setChoice(event.target.value)
              }}
            >
              {CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>{' '}
            <button
              type="button"
              className="ghost"
              disabled={busy || choice === ''}
              onClick={() => {
                void save()
              }}
            >
              Record
            </button>
          </p>
        ) : null}
        {problem === undefined ? null : (
          <p role="alert" className="failure">
            {problem}
          </p>
        )}
      </dd>
    </>
  )
}
